import { html, nothing, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhDotsThreeVertical";
import "@phosphor-icons/webcomponents/PhNotePencil";
import "@phosphor-icons/webcomponents/PhExport";
import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhTrash";
import "@phosphor-icons/webcomponents/PhArchive";
import "@phosphor-icons/webcomponents/PhImage";
import "ui-components/button/wow-button";
import "ui-components/dropdown-menu/dropdown-menu";
import "ui-components/dropdown-menu/dropdown-menu-item";

import { WowSortableTable } from "ui-components/wow-base-table/wow-sortable-table";
import type { Community } from "api-contracts/rpc/community";
import type { TableColumn } from "ui-components/wow-base-table/types";
import type { ISortableState, ISortableService } from "ui-components/wow-base-table/sort-types";
import { getAssetUrl } from "helpers/assetHelpers";
import { inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";
import type { ICreatorCommunitiesState, ICreatorCommunitiesService } from "../../services/creator-communities/types";
import { NotificationService } from "ui-components/notification";
import { styles as baseStyles } from "ui-components/wow-base-table/wow-base-table.css";
import { styles } from "./communities-table.css";
import { t, translate } from "../../translations/i18n";

@customElement("communities-table")
export class CommunitiesTable extends WowSortableTable<Community> {
  static override styles = [baseStyles, styles];

  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<ICreatorCommunitiesState>("creatorCommunitiesState")
  private communitiesState!: ICreatorCommunitiesState;

  @inject<ICreatorCommunitiesService>("creatorCommunitiesService")
  private communitiesService!: ICreatorCommunitiesService;

  @property({ type: Array })
  communities: Community[] = [];

  @property({ type: Number })
  override breakpoint = 1400;

  protected getSortState(): ISortableState<Community> {
    return this.communitiesState;
  }

  protected getSortService(): ISortableService {
    return this.communitiesService;
  }

  protected getItems(): Community[] {
    return this.communities;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.disableRowClick = true;
    this.hoverable = false;
    this.tileRender = (community) => this.renderCommunityTile(community);
  }

  override willUpdate(changed: Map<string, unknown>) {
    super.willUpdate(changed);
    this.columns = this.buildColumns();
  }

  private buildColumns(): TableColumn<Community>[] {
    return [
      {
        key: "thumbnail_url",
        header: translate("communitiesPage.columnCover"),
        render: (_value, row) => this.renderThumbnail(row),
      },
      {
        key: "title",
        header: translate("communitiesPage.columnName"),
        render: (_value, row) => html`<span>${row.title}</span>`,
      },
      {
        key: "status",
        header: translate("communitiesPage.columnStatus"),
        sortable: true,
        render: (_value, row) =>
          this.renderStatusBadge(
            row.status,
            {
              published: translate("communitiesPage.filterPublished"),
              draft: translate("communitiesPage.draft"),
              archived: translate("communitiesPage.archived"),
            },
            {
              published: "event",
              draft: "event",
              archived: "ended",
            },
          ),
      },
      {
        key: "content_tier",
        header: translate("communitiesPage.columnType"),
        render: (_value, row) => this.renderContentTier(row),
      },
      {
        key: "member_count",
        header: translate("communitiesPage.columnMembers"),
        render: (_value, row) => String(row.member_count),
      },
      {
        key: "chat_message_count",
        header: translate("communitiesPage.columnChatMessages"),
        render: (_value, row) => String(row.chat_message_count),
      },
      {
        key: "link_clicks",
        header: translate("communitiesPage.columnLinkClicks"),
        render: (_value, row) => String(row.link_clicks),
      },
      {
        key: "discount_clicks",
        header: translate("communitiesPage.columnDiscountClicks"),
        render: (_value, row) => String(row.discount_clicks),
      },
      {
        key: "action",
        header: "",
        render: (_value, row) => this.renderActionButton(row),
      },
      {
        key: "menu",
        header: "",
        render: (_value, row) => this.renderMenu(row),
      },
    ];
  }

  private renderThumbnail(community: Community) {
    if (community.thumbnail_url) {
      return html`
        <div class="thumbnail">
          <img src=${getAssetUrl(community.thumbnail_url)} alt=${community.title} />
        </div>
      `;
    }
    return html`
      <div class="thumbnail thumbnail-placeholder">
        <ph-image size="24"></ph-image>
      </div>
    `;
  }

  private renderContentTier(community: Community) {
    if (community.content_tier === "free") {
      return translate("communitiesPage.free");
    }
    if (community.fee_cents) {
      return new Intl.NumberFormat(this.locale, {
        style: "currency",
        currency: "EUR",
      }).format(community.fee_cents / 100);
    }
    return translate("communitiesPage.free");
  }

  private renderActionButton(community: Community): TemplateResult {
    if (community.status === "published") {
      return html`
        <wow-button variant="neutral" size="small" rounded @click=${() => this.emit("edit-community", { community })}>
          <ph-eye slot="prefix" size="16"></ph-eye>
          ${t("communitiesPage.viewCommunity")}
        </wow-button>
      `;
    }
    if (community.status === "draft") {
      return html`
        <wow-button variant="neutral" size="small" rounded @click=${() => this.emit("edit-community", { community })}>
          <ph-note-pencil slot="prefix" size="16"></ph-note-pencil>
          ${t("communitiesPage.editCommunity")}
        </wow-button>
      `;
    }
    return html``;
  }

  private renderMenu(community: Community) {
    return html`
      <dropdown-menu
        glass
        placement="bottom-end"
        @dropdown-item-click=${(e: CustomEvent) => this.handleMenuAction(e, community)}
      >
        <wow-button
          slot="trigger"
          variant="subtle"
          size="medium"
          rounded
          .borderColor=${"var(--sds-color-border-default-secondary)"}
          aria-label="More options"
        >
          <ph-dots-three-vertical size="20"></ph-dots-three-vertical>
        </wow-button>
        <dropdown-menu-item action="edit" icon-position="start">
          <ph-note-pencil slot="icon" size="16"></ph-note-pencil>
          ${t("communitiesPage.menuEdit")}
        </dropdown-menu-item>
        <dropdown-menu-item action="share" icon-position="start">
          <ph-export slot="icon" size="16"></ph-export>
          ${t("communitiesPage.menuShare")}
        </dropdown-menu-item>
        <dropdown-menu-item action="archive" icon-position="start">
          <ph-archive slot="icon" size="16"></ph-archive>
          ${t("communitiesPage.menuArchive")}
        </dropdown-menu-item>
      </dropdown-menu>
    `;
  }

  private renderCommunityTile(community: Community) {
    return html`
      <div class="community-tile">
        <div class="community-tile-info">
          <span class="community-tile-title">${community.title}</span>
          ${this.renderStatusBadge(
            community.status,
            {
              published: translate("communitiesPage.filterPublished"),
              draft: translate("communitiesPage.draft"),
              archived: translate("communitiesPage.archived"),
            },
            {
              published: "event",
              draft: "event",
              archived: "ended",
            },
          )}
        </div>
        <span class="community-tile-type">${this.renderContentTier(community)}</span>
        <div class="community-tile-stats">
          <span>${translate("communitiesPage.tileMembers")}: ${community.member_count.toLocaleString()}</span>
          <span
            >${translate("communitiesPage.tileChatMessages")}: ${community.chat_message_count.toLocaleString()}</span
          >
          <span
            >${translate("communitiesPage.tileClicks")}:
            ${(community.link_clicks + community.discount_clicks).toLocaleString()}</span
          >
        </div>
        <div class="community-tile-actions">
          ${this.renderTileActionButton(community)} ${this.renderMenu(community)}
        </div>
      </div>
    `;
  }

  private renderTileActionButton(community: Community) {
    if (community.status === "published") {
      return html`
        <wow-button
          variant="neutral"
          size="medium"
          rounded
          full-width
          @click=${() => this.emit("edit-community", { community })}
        >
          <ph-eye slot="prefix" size="16"></ph-eye>
          ${t("communitiesPage.viewCommunity")}
        </wow-button>
      `;
    }
    if (community.status === "draft") {
      return html`
        <wow-button
          variant="neutral"
          size="medium"
          rounded
          full-width
          @click=${() => this.emit("edit-community", { community })}
        >
          <ph-note-pencil slot="prefix" size="16"></ph-note-pencil>
          ${t("communitiesPage.editCommunity")}
        </wow-button>
      `;
    }
    return nothing;
  }

  private handleMenuAction(e: CustomEvent, community: Community) {
    const action = e.detail?.action;
    switch (action) {
      case "edit":
        this.emit("edit-community", { community });
        break;
      case "share":
        this.copyShareLink(community);
        break;
      case "archive":
        this.emit("archive-community", { community });
        break;
    }
  }

  private copyShareLink(community: Community) {
    const isProduction = window.location.hostname.endsWith("worldofwow.com");
    const baseUrl = isProduction ? "https://worldofwow.com" : "https://worldofwow.dev";
    const handle = this.authState.currentUser.get()?.handle ?? "";
    const url = `${baseUrl}/${handle}/communities/${community.id}`;
    navigator.clipboard.writeText(url).then(() => {
      NotificationService.success(translate("communitiesPage.linkCopied"));
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "communities-table": CommunitiesTable;
  }
}
